import {
  LuLayoutDashboard, LuArrowLeftRight, LuWallet, LuUndo2, LuBedDouble, LuUsers,
  LuCalendarDays, LuNewspaper, LuInbox, LuFileText, LuReceipt, LuFileCheck, LuMail,
  LuSettings, LuKeyRound, LuSearch, LuMic, LuPresentation, LuHandshake, LuCreditCard,
  LuBanknote, LuHotel, LuClipboardList, LuMegaphone,
} from 'react-icons/lu';

/*
 * Navigation per role.
 *
 *   { section: 'Label', items: [...] }          — labelled group of items
 *   { label, to, icon, match? }                 — link; `match` lists extra paths that
 *                                                 mark it active (a trailing `*` = prefix)
 *   { label, icon?, children: [...] }           — collapsible group (may nest)
 *
 * Route matching is case-insensitive, mirroring react-router's default.
 */

const paymentReceiptConfig = (prefix) => {
  const p = (s) => (prefix ? `/${prefix}${s.charAt(0).toUpperCase()}${s.slice(1)}` : `/${s}`);
  return [
    { label: 'Currency Type', to: p('prCurrencyType') },
    { label: 'Event Type', to: p('prEventType') },
    { label: 'Occupancy Type', to: p('prOccupancyType') },
    { label: 'Payment Type', to: p('prPaymentType') },
    { label: 'Registration Type', to: p('prRegistrationType') },
    { label: 'Registration Category', to: p('prRegistrationCategory') },
  ];
};

const ADMIN_NAV = [
  {
    items: [{ label: 'Dashboard', to: '/adminDashboard', icon: LuLayoutDashboard }],
  },
  {
    section: 'Revenue Management',
    items: [
      { label: 'Transaction Status', to: '/transactionStatus', icon: LuArrowLeftRight },
      {
        label: 'Revenue Status', icon: LuWallet,
        children: [
          { label: 'All Revenue', to: '/revenueAll' },
          { label: 'Conference Revenue', to: '/revenueConference' },
          { label: 'Webinar Revenue', to: '/revenueWebinar' },
        ],
      },
      {
        label: 'Refund Status', icon: LuUndo2,
        children: [
          { label: 'Approved Refunds', to: '/refundApproved' },
          { label: 'Pending Refunds', to: '/refundPending' },
        ],
      },
    ],
  },
  {
    section: 'Conference Management',
    items: [
      {
        label: 'Accommodation Status', icon: LuBedDouble,
        children: [
          { label: 'Paid', to: '/paidAccommodation' },
          { label: 'Free', to: '/freeAccommodation' },
        ],
      },
      {
        label: 'Participant Form', icon: LuUsers,
        children: [
          {
            label: 'Free Participant Form',
            children: [
              { label: 'Pending', to: '/pendingFreeParticipantForm' },
              { label: 'Approved', to: '/approvedFreeParticipantForm' },
            ],
          },
          {
            label: 'Onsite Participant Form',
            children: [
              { label: 'Pending', to: '/pendingOnsiteParticipantForm' },
              { label: 'Approved', to: '/approvedOnsiteParticipantForm' },
            ],
          },
        ],
      },
    ],
  },
  {
    section: 'Event Management',
    items: [
      {
        label: 'Event Setup', icon: LuCalendarDays,
        children: [
          { label: 'Conference Event', to: '/conferenceEvent' },
          { label: 'Webinar Event', to: '/webinarEvent' },
        ],
      },
      {
        label: 'Blogs', icon: LuNewspaper,
        children: [
          { label: 'Pending Blogs', to: '/pendingBlogs' },
          { label: 'Approved Blogs', to: '/approvedBlogs' },
        ],
      },
    ],
  },
  {
    section: 'Conference Leads',
    items: [
      {
        label: 'Leads Forms', icon: LuInbox,
        children: [
          { label: 'Abstract', to: '/abstract' },
          { label: 'Brochure', to: '/brochure' },
          { label: 'Contact', to: '/contact' },
          { label: 'Proceedings Lists', to: '/pastconfProceedingsLists' },
        ],
      },
      {
        label: 'Revenue Forms', icon: LuCreditCard,
        children: [
          { label: 'Registration', to: '/registration' },
          { label: 'Self Registration', to: '/selfRegistration' },
          { label: 'Paypal Transactions', to: '/paypalTransactions' },
          { label: 'Razorpay Transactions', to: '/razorpayTransactions' },
        ],
      },
    ],
  },
  {
    section: 'PDF Generation',
    items: [
      {
        label: 'Payment Receipt', icon: LuReceipt,
        children: [
          { label: 'Received Receipt', to: '/paymentReceiptReceived', match: ['/paymentReceiptPdf'] },
          { label: 'Generated Receipt', to: '/paymentReceiptGenerated' },
          {
            label: 'Configuration',
            children: [
              ...paymentReceiptConfig(),
              { label: 'Participant Payment Type', to: '/prParticipantPaymentType' },
            ],
          },
        ],
      },
      {
        label: 'Invoice Receipt', icon: LuFileText,
        children: [
          { label: 'Received Receipt', to: '/invoiceReceiptReceived', match: ['/invoiceReceiptPdf'] },
          { label: 'Generated Receipt', to: '/invoiceReceiptGenerated' },
          {
            label: 'Configuration',
            children: [
              { label: 'USA Bank', to: '/invoiceUsBank' },
              { label: 'Indian Bank', to: '/invoiceInBank' },
              { label: 'Registration Type', to: '/invoiceRegType' },
            ],
          },
        ],
      },
      {
        label: 'Abstract Receipt', icon: LuFileCheck,
        children: [
          {
            label: 'Conference Manager',
            children: [
              { label: 'Pending', to: '/pendingProgramManager' },
              { label: 'Approved', to: '/approvedProgramManager' },
            ],
          },
          {
            label: 'Reviewers',
            children: [
              { label: 'Pending', to: '/pendingReviewers' },
              { label: 'Approved', to: '/approvedReviewers' },
              { label: 'All Reviewers', to: '/allReviewers' },
            ],
          },
          {
            label: 'Configurations',
            children: [
              { label: 'Abstract Type', to: '/abstractType' },
              { label: 'Abstract Category', to: '/abCategory' },
            ],
          },
        ],
      },
      {
        label: 'Invitation Letter', icon: LuMail,
        children: [
          { label: 'Current Invitation Letter', to: '/currentInvitationLetter' },
          { label: 'All Invitation Letter', to: '/allInvitationLetter' },
          {
            label: 'Configurations',
            children: [{ label: 'Invitation Category', to: '/invitationCategory' }],
          },
        ],
      },
    ],
  },
  {
    section: 'General Configuration',
    items: [
      {
        label: 'Default Setup', icon: LuSettings,
        children: [
          { label: 'Subject Type', to: '/esSubjectType' },
          { label: 'Subdomain Type', to: '/esSubdomainType' },
          { label: 'Venue Type', to: '/esVenue' },
          { label: 'Series Type', to: '/esSeriesType' },
          { label: 'Series Setup', to: '/esSeriesSetup' },
          { label: 'Hotel Setup', to: '/hotelSetupList', match: ['/hotelSetup'] },
          { label: 'Countries Type', to: '/esCountries' },
        ],
      },
      { label: 'CMS Login Configuration', to: '/cmsUser', icon: LuKeyRound },
      {
        label: 'SEO Config', icon: LuSearch,
        children: [
          { label: 'Meta Tag Pages', to: '/metaTags' },
          { label: 'SEO Context Page', to: '/seoContextPage' },
        ],
      },
    ],
  },
];

const EVENT_NAV = [
  {
    items: [{ label: 'Dashboard', to: '/dashboard', icon: LuLayoutDashboard }],
  },
  {
    section: 'Conference Leads',
    items: [
      {
        label: 'Leads Forms', icon: LuInbox,
        children: [
          { label: 'Abstract', to: '/emabstract' },
          { label: 'Brochure', to: '/embrochure' },
          { label: 'Contact', to: '/emcontact' },
          { label: 'Proceedings', to: '/empastconfProceedingsLists' },
        ],
      },
      {
        label: 'Revenue Forms', icon: LuCreditCard,
        children: [
          { label: 'Registration', to: '/emregistration' },
          { label: 'Self Registration', to: '/emselfRegistration' },
        ],
      },
      {
        label: 'Sponsorship Forms', icon: LuHandshake,
        children: [
          { label: 'Sponsorship Registrations', to: '/emRegistrationSponsorshipList', match: ['/emRegistrationSponsorship', '/emRegistrationSponsorship/*'] },
          { label: 'Sponsorship Transactions', to: '/emRegistrationSponsorshipTransaction' },
          { label: 'Sponsorship Inquiries', to: '/emSponsorshipInquiryList' },
        ],
      },
    ],
  },
  {
    section: 'Receipt Generation',
    items: [
      {
        label: 'Abstract Receipt', icon: LuFileCheck,
        children: [
          { label: 'Abstract Receipt', to: '/abstractReceipt', match: ['/abstractReceiptPdf'] },
          {
            label: 'Configuration',
            children: [
              { label: 'Conference Manager', to: '/programManager' },
              { label: 'Reviewers', to: '/abReviewers' },
            ],
          },
        ],
      },
      { label: 'Payment Receipt Form', to: '/paymentReceipt', icon: LuReceipt },
      { label: 'Invoice Receipt Form', to: '/invoiceReceipt', icon: LuFileText },
      { label: 'Refund Form', to: '/refund', icon: LuUndo2 },
      { label: 'Invitation Letter', to: '/invitationLetter', icon: LuMail, match: ['/invitationLetterPdf'] },
      { label: 'Accommodation Form', to: '/accommodation', icon: LuHotel },
    ],
  },
  {
    section: 'Conference Management',
    items: [
      {
        label: 'Participant Form', icon: LuUsers,
        children: [
          { label: 'Free Participant Form', to: '/freeParticipantForm' },
          { label: 'Onsite Participant Form', to: '/onsiteParticipantForm' },
        ],
      },
      {
        label: 'Conference Program', icon: LuPresentation,
        children: [
          { label: 'Participant Setup', to: '/participantSetupList', match: ['/participantSetup*'] },
          { label: 'Presentation Setup', to: '/presentationSetupList', match: ['/presentationSetup*'] },
        ],
      },
      { label: 'Blogs', to: '/blogs', icon: LuNewspaper },
    ],
  },
  {
    section: 'Onsite Management',
    items: [
      { label: 'Introduction Speech Form', to: '/eventIntroductionSpeech', icon: LuMegaphone, match: ['/introductionspeech-view'] },
      { label: 'Moderator Speech Form', to: '/eventModeratorSpeech', icon: LuMic, match: ['/moderatorspeech-view'] },
    ],
  },
];

const FINANCE_NAV = [
  {
    items: [{ label: 'Revenue', to: '/revenue', icon: LuWallet }],
  },
];

const MANAGEMENT_NAV = [
  {
    items: [{ label: 'Dashboard', to: '/mgmtDashboard', icon: LuLayoutDashboard }],
  },
  {
    section: 'PDF Generation',
    items: [
      {
        label: 'Payment Receipt', icon: LuReceipt,
        children: [
          { label: 'Received Receipt', to: '/mgmtPaymentReceiptReceived', match: ['/mgmtPaymentReceiptPdf'] },
          { label: 'Generated Receipt', to: '/mgmtPaymentReceiptGenerated' },
          { label: 'Configuration', children: paymentReceiptConfig('mgmt') },
        ],
      },
      {
        label: 'Invoice Receipt', icon: LuFileText,
        children: [
          { label: 'Received Receipt', to: '/mgmtInvoiceReceiptReceived', match: ['/mgmtInvoiceReceiptPdf'] },
          { label: 'Generated Receipt', to: '/mgmtInvoiceReceiptGenerated' },
          {
            label: 'Configuration',
            children: [
              { label: 'USA Bank', to: '/mgmtInvoiceUsBank' },
              { label: 'Indian Bank', to: '/mgmtInvoiceInBank' },
              { label: 'Registration Type', to: '/mgmtInvoiceRegType' },
            ],
          },
        ],
      },
    ],
  },
  {
    section: 'Conference Management',
    items: [
      {
        label: 'Accommodation Status', icon: LuBedDouble,
        children: [
          { label: 'Paid', to: '/mgmtPaidAccommodation' },
          { label: 'Free', to: '/mgmtFreeAccommodation' },
        ],
      },
      {
        label: 'Accommodation', icon: LuHotel,
        children: [
          { label: 'Waiting Accommodation', to: '/mgmtWaitingAccommodation' },
          { label: 'Pending Accommodation', to: '/mgmtPendingAccommodation' },
          { label: 'Staging Accommodation', to: '/mgmtStagingAccommodation' },
          { label: 'Verified Accommodation', to: '/mgmtVerifiedAccommodation' },
          { label: 'Cancelled Accommodation', to: '/mgmtCancelledAccommodation' },
          { label: 'Deleted Accommodation', to: '/mgmtDeletedAccommodation' },
          {
            label: 'Configuration',
            children: [{ label: 'Accommodation Setup', to: '/accommodationSetup' }],
          },
        ],
      },
    ],
  },
  {
    section: 'Revenue Management',
    items: [
      { label: 'Transaction Status', to: '/mgmtTransactionStatus', icon: LuArrowLeftRight },
      { label: 'Paypal Transactions', to: '/mgmtPaypalTransactions', icon: LuBanknote },
      {
        label: 'Revenue Status', icon: LuWallet,
        children: [
          { label: 'All Revenue', to: '/mgmtRevenueAll' },
          { label: 'Conference Revenue', to: '/mgmtRevenueConference' },
          { label: 'Webinar Revenue', to: '/mgmtRevenueWebinar' },
        ],
      },
      {
        label: 'Refund Status', icon: LuClipboardList,
        children: [
          { label: 'Approved Refunds', to: '/mgmtRefundApproved' },
          { label: 'Pending Refunds', to: '/mgmtRefundPending' },
        ],
      },
    ],
  },
];

export const NAV_BY_ROLE = {
  admin: ADMIN_NAV,
  event: EVENT_NAV,
  finance: FINANCE_NAV,
  management: MANAGEMENT_NAV,
};

export const ROLE_LABELS = {
  admin: 'Admin',
  event: 'Event',
  finance: 'Finance',
  management: 'Management',
  onsite: 'Onsite',
  onsitecoordinator: 'Onsite Coordinator',
};

export const HOME_BY_ROLE = {
  admin: '/adminDashboard',
  event: '/dashboard',
  finance: '/revenue',
  management: '/mgmtDashboard',
};

const normalize = (path) => (path || '').replace(/\/+$/, '').toLowerCase() || '/';

/** True when `pathname` is the item's route or one of its `match` patterns. */
export const isItemActive = (item, pathname) => {
  const current = normalize(pathname);
  const patterns = [item.to, ...(item.match || [])].filter(Boolean);
  return patterns.some((pattern) => {
    // Prefix patterns keep their trailing slash, so '/x/*' matches '/x/42' but not '/xy'.
    if (pattern.endsWith('*')) return `${current}/`.startsWith(pattern.slice(0, -1).toLowerCase());
    return current === normalize(pattern);
  });
};

/** True when any descendant link of a group is active. */
export const groupHasActive = (group, pathname) =>
  (group.children || []).some((child) =>
    child.children ? groupHasActive(child, pathname) : isItemActive(child, pathname)
  );

/**
 * Resolve the breadcrumb trail for the current route: [section?, ...group labels, page label].
 * Returns null when the route isn't in the navigation.
 */
export const findTrail = (role, pathname) => {
  const sections = NAV_BY_ROLE[role] || [];
  const walk = (items, trail) => {
    for (const item of items) {
      if (item.children) {
        const found = walk(item.children, [...trail, item.label]);
        if (found) return found;
      } else if (isItemActive(item, pathname)) {
        return [...trail, item.label];
      }
    }
    return null;
  };
  for (const section of sections) {
    const found = walk(section.items, section.section ? [section.section] : []);
    if (found) return found;
  }
  return null;
};

/** Flat list of every link in a role's navigation (used by tests and lookups). */
export const flattenLinks = (role) => {
  const out = [];
  const walk = (items) => items.forEach((item) => (item.children ? walk(item.children) : out.push(item)));
  (NAV_BY_ROLE[role] || []).forEach((section) => walk(section.items));
  return out;
};
